import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { Alert, Box, Button, Paper, Stack, Typography } from '@mui/material';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import type { FundSetup } from '../../data/types';
import { canActivate, sectionValid } from '../../domain/setup/setupRules';
import { ACTIVATION_SECTIONS, sectionById } from '../../domain/setup/sections';
import { useRegistry } from '../../store/RegistryStore';
import { useToast } from '../../ui/feedback';

function Row({
  label,
  detail,
  state,
  action,
}: {
  label: string;
  detail: string;
  state: 'done' | 'todo' | 'optional';
  action?: React.ReactNode;
}) {
  return (
    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 1.25 }}>
      {state === 'done' ? (
        <CheckCircleIcon color="success" />
      ) : state === 'todo' ? (
        <ErrorOutlineIcon color="warning" />
      ) : (
        <RadioButtonUncheckedIcon color="disabled" />
      )}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {label}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {detail}
        </Typography>
      </Box>
      {action}
    </Stack>
  );
}

export default function ActivationPanel({
  setup,
  onGoTo,
  onNext,
}: {
  setup: FundSetup;
  onGoTo: (sectionId: string) => void;
  onNext: () => void;
}) {
  const { dispatch } = useRegistry();
  const toast = useToast();
  const active = setup.status !== 'DRAFT';
  return (
    <>
      <Typography variant="h5" component="h2">
        Activate register
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Creates the trust and its unit class on the Fides register. Nothing is visible to investors
        until you publish the offer.
      </Typography>
      <Paper variant="outlined" sx={{ px: 2.5, py: 1, mb: 2 }}>
        <Typography variant="overline" color="text.secondary">
          Required
        </Typography>
        {ACTIVATION_SECTIONS.map((id) => {
          const section = sectionById(id)!;
          const valid = sectionValid(id, setup.values);
          return (
            <Row
              key={id}
              label={section.title}
              detail={valid ? 'Complete' : 'Some fields need attention'}
              state={valid ? 'done' : 'todo'}
              action={
                !valid && (
                  <Button size="small" onClick={() => onGoTo(id)}>
                    Fix
                  </Button>
                )
              }
            />
          );
        })}
        <Typography variant="overline" color="text.secondary" component="div" sx={{ mt: 1 }}>
          Optional
        </Typography>
        <Row
          label="Import existing investors"
          detail="Only for a trust moving from another registry. A new deal starts empty."
          state="optional"
          action={
            <Button size="small" component={RouterLink} to="/manager/migration">
              Migration
            </Button>
          }
        />
        <Row
          label="Import historical transactions"
          detail="Opening balances and history, through the same migration tool."
          state="optional"
        />
      </Paper>
      {active ? (
        <Alert
          severity="success"
          action={
            <Button
              color="inherit"
              size="small"
              component={RouterLink}
              to={`/manager/funds/${setup.fundId}`}
            >
              View fund
            </Button>
          }
        >
          Register active. {String(setup.values['fund.name'])} and its{' '}
          {String(setup.values['class.name'])} class are on the register.
        </Alert>
      ) : (
        <Alert severity="info" icon={false}>
          In production, a second person approves the trust bank details.
        </Alert>
      )}
      <Stack direction="row" justifyContent="space-between" sx={{ mt: 3 }}>
        <Button onClick={() => onGoTo('class')}>Back</Button>
        {active ? (
          <Button variant="contained" onClick={onNext}>
            Set up the offer
          </Button>
        ) : (
          <Button
            variant="contained"
            disabled={!canActivate(setup)}
            onClick={() => {
              dispatch({ type: 'SETUP_ACTIVATE', id: setup.id });
              toast('Register activated');
            }}
          >
            Activate register
          </Button>
        )}
      </Stack>
    </>
  );
}
